import {useMutation, UseMutationResult} from '@tanstack/react-query';
import {useLocale} from 'state/locale';
import Api from 'types/api';
import {toSnakeCase} from 'utils/object';
import {ContentType, request} from 'utils/request-axios';

import {CustomTemplateDuplicateFormData} from './Duplicate/types';
import {CustomTemplateCreateFormData} from './types';

export const useCustomTemplateCreate = (
    documentType: string,
): UseMutationResult<
    {
        mid: string;
    },
    Error,
    CustomTemplateCreateFormData
> => {
    const locale = useLocale();

    return useMutation({
        mutationFn: (_formData) => {
            const formData = new FormData();
            if (_formData.uploadedFile) {
                formData.append('uploaded_file', _formData.uploadedFile);
            }
            formData.append('base_template_code', _formData.baseTemplateCode);
            formData.append('display_name', _formData.displayName);
            formData.append('document_title', _formData.documentTitle);
            if (_formData.useAdvancedTaxSettings) {
                formData.append('use_advanced_tax_settings', 'on');
            }

            return request({
                contentType: ContentType.FORM_DATA,
                formData: formData,
                locale,
                method: 'POST',
                url: `/document-template/${documentType}/create-from-file/`,
            }).then((response) => response.data);
        },
    });
};

type DeleteCustomTemplatePossibleResponses =
    Api.Response.RedirectAxios<'customtemplate_list_view'>;

export const useDeleteCustomTemplate = (): UseMutationResult<
    DeleteCustomTemplatePossibleResponses,
    Error,
    {
        mid: string;
    }
> => {
    const locale = useLocale();

    return useMutation({
        mutationFn: ({mid}) =>
            request({
                locale,
                method: 'POST',
                url: window.Urls.customtemplate_delete_view(mid),
            }),
    });
};

type DuplicatePossibleResponses = Api.Response.SuccessAxios<
    'customtemplate' | 'file_based_customtemplate',
    {mid: string}
>;

export const useDuplicateCustomTemplate = (
    mid: string,
): UseMutationResult<
    DuplicatePossibleResponses,
    Error,
    CustomTemplateDuplicateFormData
> => {
    const locale = useLocale();

    return useMutation({
        mutationFn: (formData) =>
            request({
                formData: toSnakeCase(formData),
                locale,
                method: 'POST',
                url: window.Urls.customtemplate_duplicate_view(mid),
            }),
    });
};
